import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Users, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatPrice, formatClassDate, formatTimeRange, formatDate, formatTime } from "@/lib/api";
import { HOME } from "@/constants/testIds";

export default function ClassCard({ cls }) {
  const spots = cls.spots_remaining ?? 0;
  const sold_out = spots <= 0;
  const urgent = spots > 0 && spots <= 5;

  const fullDate = cls.class_date ? formatClassDate(cls.class_date) : formatDate(cls.date_time_iso);
  const month = fullDate.split(",")[1]?.trim().split(" ")[0] || "";
  const timeRange = formatTimeRange(cls.start_time, cls.end_time) || formatTime(cls.date_time_iso);

  return (
    <article
      className="card-lift group relative flex flex-col rounded-2xl border border-gg-border bg-gg-surface p-6 sm:p-8"
      data-testid={HOME.classCard(cls.id)}
    >
      {/* Spots badge */}
      <div className="flex items-start justify-between gap-3">
        <span className="font-script text-3xl text-gg-green leading-none">
          {month}
        </span>
        <Badge
          variant="outline"
          className={`border-0 rounded-full px-3 py-1.5 text-xs font-semibold ${
            sold_out
              ? "bg-[#B24C4C]/10 text-[#B24C4C]"
              : urgent
              ? "bg-[#D9933B]/15 text-[#9b6a26]"
              : "bg-gg-green/15 text-gg-green"
          }`}
          data-testid={HOME.classSpotsBadge(cls.id)}
        >
          {sold_out ? "Sold Out" : `${spots} ${spots === 1 ? "spot" : "spots"} remaining`}
        </Badge>
      </div>

      <h3 className="font-heading text-2xl sm:text-3xl text-[var(--gg-text)] mt-4 leading-tight">
        {cls.title}
      </h3>

      <div className="mt-5 space-y-2 text-sm text-gg-muted">
        <p className="flex items-center gap-2">
          <Calendar size={15} className="text-gg-green" />
          {fullDate}
        </p>
        <p className="flex items-center gap-2">
          <Clock size={15} className="text-gg-green" />
          {timeRange}
        </p>
        <p className="flex items-center gap-2">
          <MapPin size={15} className="text-gg-green" />
          Chino Valley, AZ
        </p>
        <p className="flex items-center gap-2">
          <Users size={15} className="text-gg-green" />
          Capacity {cls.capacity_total}
        </p>
      </div>

      <p className="mt-5 text-sm text-[var(--gg-text)]/85 line-clamp-4 leading-relaxed">
        {cls.description}
      </p>

      <div className="mt-6 pt-5 border-t border-gg-border flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">
            Student
          </p>
          <p className="font-heading text-3xl text-[var(--gg-text)] leading-none mt-1">
            {formatPrice(cls.price_student)}
          </p>
          {Number(cls.price_parent) > 0 && cls.allow_parent_addon !== false && (
            <p className="text-xs text-gg-muted mt-1">
              + {formatPrice(cls.price_parent)} optional parent add-on
            </p>
          )}
        </div>
        <Link
          to={sold_out ? `/waitlist/${cls.id}` : `/register/${cls.id}`}
          className={`btn-pill ${sold_out ? "btn-outline" : "btn-primary"}`}
          data-testid={HOME.classRegisterBtn(cls.id)}
        >
          {sold_out ? "Join Waitlist" : "Register"} <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
}
